import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Table from '../Components/Tables';
import Button from '../Components/Buttons';

import { PromptApiProvider, usePromptApi } from '../Services/PromptApiProvider';

import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import { AlertProvider, useAlert } from '../Components/AlertContext';
import { formatDateUTC } from '../utils/formatDateUTC';

function PromptsHub({canEdit}) {

    const navigate = useNavigate();
    const location = useLocation();
    const { organizationId } = useParams(); 
    const { showAlert } = useAlert();
    const { showConfirmation } = useConfirmation();

    const { getPrompts, deletePrompt, createPrompt } = usePromptApi();
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [prompts, setPrompts] = useState([]);
    const [totalPrompts, setTotalPrompts] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [alertShown, setAlertShown] = useState(false);

    const [selectedRows, setSelectedRows] = useState([]);
    const selectedId = selectedRows.length === 1 ? selectedRows[0].id : null;

    const columns = [
        { name: 'ID', selector: (row) => row.id, sortable: false }, 
        { name: 'Name', selector: (row) => row.name, sortable: false },
        { name: 'Status', selector: (row) => row.is_active === true ? "Active" : "InActive", sortable: false },
        { name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: false },
        { name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: false },
    ];

     // Display alert message on navigation
    useEffect(() => {
        if (location.state?.successMessage && !alertShown) {
            showAlert('success', location.state.successMessage);
            setAlertShown(true);
            navigate(location.pathname, { replace: true, state: {} });
        }
        if (location.state?.returnMessage && !alertShown) {
            showAlert('help', location.state.returnMessage);
            setAlertShown(true);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state?.successMessage, alertShown, navigate, location.pathname, showAlert]);

    const handleDelete = async (selectedId) => {
        if (!selectedId) return;
        showConfirmation(
            `Are you sure you want to delete the selected prompt?`,
            async () => {
                try {
                  setLoading(true);
                  await deletePrompt(selectedId);
                  featchPrompts(0, itemsPerPage);
                  showAlert('success', 'Prompt was successfully deleted!');
                } catch (error) {
                  showAlert('fail', 'Failed to delete the prompt');
                }
            }
        );
    };

    const createCopy = async () => {
        if (!selectedId) return;
        try {
            setLoading(true);
            const promptToCopy = prompts.find(prompt => prompt.id === selectedId);

            if (!promptToCopy) {
                showAlert('fail', 'Prompt not found');
                setLoading(false);
                return;
            }
            const baseName = promptToCopy.name;
            const copyNumbers = prompts
                .map(a => {
                    const match = a.name.match(new RegExp(`^${baseName} \\(Copy (\\d+)\\)$`));
                    return match ? parseInt(match[1], 10) : null;
                })
                .filter(n => n !== null);
            const nextCopyNumber = copyNumbers.length > 0 ? Math.max(...copyNumbers) + 1 : 1;

            const newPromptData = {
                name: `${baseName} (Copy ${nextCopyNumber})`,
                pattern_type: promptToCopy.pattern_type,
                definition: promptToCopy.definition
            };
            await createPrompt(newPromptData);
            await featchPrompts(0, itemsPerPage);
            showAlert('success', 'Prompt copy was successfully created!');
        } catch (error) {
            showAlert('fail', 'Failed to create a copy of the prompt');
        } finally {
            setLoading(false);
        }
    }
    

    const handleRowClicked = (row) => {
        navigate(`/organizations/${organizationId}/prompts-hub/${row.id}`);
    };

    const featchPrompts = async (skip, limit) => {
        if (limit !== itemsPerPage) {
            setItemsPerPage(limit);
        }

        setLoading(true);

        try {
            const response = await getPrompts(skip, limit);
            setPrompts(response.items);
            setTotalPrompts(response.totalCount); // Set the total number of prompts
        } catch (error) {
            console.error('Error fetching prompts:', error);
            setError(error.message); // Set error message
        } finally {
            setLoading(false); // Stop loading
        }
    }

    useEffect(() => {
        featchPrompts(0, itemsPerPage);
    }, []);

    return (
        <div className="container">
            <Breadcrumbs />
            <div className="row d-flex gap">
                <div className="radius-yes p-2 bg-yes col-12">
                    {error ? (
                        <div>Error: {error}</div>
                    ) : (
                        <>
                            <Table
                                title="Prompts"
                                columns={columns}
                                data={prompts} // Render table only when data is available
                                selectableRows
                                selectableRowsHighlight
                                progressPending={loading}
                                paginationPerPage={itemsPerPage}
                                paginationTotalRows={totalPrompts}
                                disabled={false}
                                onRowClicked={handleRowClicked}
                                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                                onDelete={handleDelete}
                                onChangePage={featchPrompts}
                                getRowUrl={(row) => `/organizations/${organizationId}/prompts-hub/${row.id}`}
                                onSelectedRowsChange={({ selectedRows }) => {
                                    setSelectedRows(selectedRows);
                                }}
                            />
                            {canEdit &&(
                                <Button
                                    background="color2"
                                    size="large"
                                    onClick={() =>
                                        navigate(`/organizations/${organizationId}/prompts-hub/new-prompt`)
                                    }
                                >
                                    Create New Prompt
                                </Button>
                            )}
                            {selectedId && (
                                <Button variant="ghost" size="medium" onClick={createCopy}>
                                    Create copy
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function PromptsHubWrapper({canEdit}) {
    return (
        <AlertProvider>
            <ConfirmationProvider>
                <PromptApiProvider>
                    <PromptsHub canEdit={canEdit}/>
                </PromptApiProvider>
            </ConfirmationProvider>
        </AlertProvider>
    );
}

export default PromptsHubWrapper;